import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toIdeaMode, type IdeaMode } from "@mycompbox/shared";
import { meKeys, useMe } from "@/hooks/queries/use-me";
import { ApiError } from "@/lib/api/client";
import { usersApi } from "@/lib/api/users";

/**
 * Each person's default for how their ideas move after approval (ER-145):
 * Manual (the default) or Autopilot. Saved on their account, so it follows
 * them to every browser; any one idea can still be switched on its page.
 */

const OPTIONS: { value: IdeaMode; label: string }[] = [
  { value: "manual", label: "Manual" },
  { value: "autopilot", label: "Autopilot" },
];

export function IdeaModeSetting() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (mode: IdeaMode) => {
      if (!me) throw new Error("Not signed in");
      return usersApi.update(me.id, { ideaModeDefault: mode });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: meKeys.all }),
  });
  const mode = (save.isPending ? save.variables : undefined) ?? toIdeaMode(me?.ideaModeDefault);

  return (
    <div className="flex flex-col gap-2 border-b border-border pb-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <div className="text-sm font-medium text-foreground">Default mode for ideas</div>
        <div className="text-[13px] text-muted-foreground">
          Manual: you make the blueprint, start the build and merge the PRs. Autopilot: after you approve, it does all
          that by itself. You can switch any idea on its page.
        </div>
      </div>
      <div className="flex flex-col gap-1 sm:w-56">
        <select
          aria-label="Default mode for ideas"
          value={mode}
          disabled={!me}
          onChange={(e) => {
            save.mutate(toIdeaMode(e.target.value));
          }}
          className="min-h-11 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {save.error && (
          <p role="alert" className="text-xs text-destructive">
            {save.error instanceof ApiError ? save.error.message : "Couldn't save. Try again."}
          </p>
        )}
      </div>
    </div>
  );
}
